/**
 * Override Form Component
 *
 * Modal form for creating or editing SLA attribute overrides for clients.
 * Supports per-attribute validation and optional expiration dates.
 *
 * @module components/SLA/OverrideForm
 */

import React, { useState, useEffect } from 'react';
import { ClientSLAOverride, SLAAttributeName } from '@shared/types/sla';
import slaService from '../../services/slaService';
import { Modal, Button } from '../ui';

interface Client {
  client_id: string;
  client_name: string;
}

interface OverrideFormProps {
  /** Existing override to edit (undefined for create) */
  override?: ClientSLAOverride;
  /** Callback when override is successfully created/updated */
  onOverrideChanged: () => void;
  /** Callback to close the modal */
  onClose: () => void;
}

const ATTRIBUTES: { value: SLAAttributeName; label: string; unit: string }[] = [
  { value: 'uptime_percent', label: 'Uptime %', unit: '%' },
  { value: 'rto_hours', label: 'RTO', unit: 'hours' },
  { value: 'rpo_hours', label: 'RPO', unit: 'hours' },
  { value: 'penalty_per_hour_usd', label: 'Penalty Rate', unit: '$/hour' },
];

/**
 * Override Form - Create/edit SLA overrides
 */
const OverrideForm: React.FC<OverrideFormProps> = ({ override, onOverrideChanged, onClose }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(override?.client_id || '');
  const [selectedAttribute, setSelectedAttribute] = useState<SLAAttributeName>(
    override?.attribute_name || 'uptime_percent'
  );
  const [overrideValue, setOverrideValue] = useState(override?.override_value?.toString() || '');
  const [expirationDate, setExpirationDate] = useState(
    override?.expiration_date ? new Date(override.expiration_date).toISOString().slice(0, 16) : ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    validateValue(overrideValue);
  }, [selectedAttribute, overrideValue]);

  const loadClients = async () => {
    // Mock clients - replace with actual API call
    setClients([
      { client_id: 'client-1', client_name: 'Client A' },
      { client_id: 'client-2', client_name: 'Client B' },
    ]);
  };

  const validateValue = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setValidationError(null);
      return true; // Empty is valid, will be caught by required
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      setValidationError('Must be a valid number');
      return false;
    }

    switch (selectedAttribute) {
      case 'uptime_percent':
        if (numValue < 90 || numValue > 99.99) {
          setValidationError('Uptime must be between 90% and 99.99%');
          return false;
        }
        break;
      case 'rto_hours':
      case 'rpo_hours':
        if (numValue <= 0 || numValue > 168) {
          setValidationError('RTO/RPO must be between 0 and 168 hours');
          return false;
        }
        break;
      case 'penalty_per_hour_usd':
        if (numValue < 0) {
          setValidationError('Penalty must be non-negative');
          return false;
        }
        break;
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateValue(overrideValue)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = {
        client_id: selectedClientId,
        attribute_name: selectedAttribute,
        override_value: parseFloat(overrideValue),
        expiration_date: expirationDate || undefined,
      };

      if (override) {
        await slaService.updateOverride(override.id, data);
      } else {
        await slaService.createOverride(data);
      }

      onOverrideChanged();
    } catch (err) {
      console.error('Failed to save override:', err);
      setError(err instanceof Error ? err.message : 'Failed to save override');
    } finally {
      setLoading(false);
    }
  };

  const selectedAttributeInfo = ATTRIBUTES.find((a) => a.value === selectedAttribute);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size='sm'
      title={override ? 'Edit SLA Override' : 'New SLA Override'}
      footer={
        <>
          <Button type='button' variant='outline' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type='submit' form='sla-override-form' disabled={loading || !!validationError}>
            {loading ? 'Saving...' : override ? 'Update Override' : 'Create Override'}
          </Button>
        </>
      }
    >
      <p className='text-sm text-[var(--color-text-secondary)]'>
        Override a specific SLA attribute for a client. The override will be applied on top
        of the base tier.
      </p>

      {error && (
        <div className='mt-4 rounded-md bg-red-50 dark:bg-red-900/20 p-4'>
          <div className='text-sm text-red-800 dark:text-red-300'>{error}</div>
        </div>
      )}

      <form id='sla-override-form' onSubmit={handleSubmit} className='mt-6 space-y-4'>
        {/* Client Selection */}
        <div>
          <label htmlFor='client' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Client *
          </label>
          <select
            id='client'
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            required
            disabled={!!override}
            className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] disabled:bg-[var(--color-bg-subtle)] sm:text-sm'
          >
            <option value=''>Select a client</option>
            {clients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>

        {/* Attribute Selection */}
        <div>
          <label htmlFor='attribute' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Attribute *
          </label>
          <select
            id='attribute'
            value={selectedAttribute}
            onChange={(e) => setSelectedAttribute(e.target.value as SLAAttributeName)}
            required
            disabled={!!override}
            className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] disabled:bg-[var(--color-bg-subtle)] sm:text-sm'
          >
            {ATTRIBUTES.map((attr) => (
              <option key={attr.value} value={attr.value}>
                {attr.label}
              </option>
            ))}
          </select>
        </div>

        {/* Override Value */}
        <div>
          <label htmlFor='value' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Override Value * {selectedAttributeInfo && `(${selectedAttributeInfo.unit})`}
          </label>
          <input
            type='number'
            id='value'
            step='any'
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            required
            className={`mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm ${
              validationError ? 'border-red-300 dark:border-red-700' : ''
            }`}
          />
          {validationError && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{validationError}</p>}
          {selectedAttribute === 'uptime_percent' && (
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>Range: 90-99.99</p>
          )}
          {(selectedAttribute === 'rto_hours' || selectedAttribute === 'rpo_hours') && (
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>Range: 0-168 hours</p>
          )}
          {selectedAttribute === 'penalty_per_hour_usd' && (
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>Must be non-negative</p>
          )}
        </div>

        {/* Expiration Date */}
        <div>
          <label htmlFor='expiration' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Expiration Date (Optional)
          </label>
          <input
            type='datetime-local'
            id='expiration'
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
          />
          <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>Leave empty for permanent override</p>
        </div>

        {/* Reason */}
      </form>
    </Modal>
  );
};

export default OverrideForm;
export { OverrideForm };
